import {
  CriteriaChecklistNode,
  CriteriaGroup,
  CriteriaNode,
  CriteriaStatement,
} from "./criteria-tree-builder-types";

/**
 * Id factory for newly created statements/groups.
 *
 * These ids only need to be unique within a session — they serve as React keys
 * and the lookup keys for the tree-mutation helpers. The canonical dotted ids
 * that the backend persists (`1`, `1.1`, `1.2.1`, …) are assigned by the
 * consuming app on first save, not here (see `CriteriaChecklistNode`).
 */
const uid = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for environments without Web Crypto (old jsdom).
  return `cb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const createEmptyStatement = (): CriteriaStatement => ({
  type: "statement",
  id: uid(),
  value: "",
});

export const createEmptyGroup = (): CriteriaGroup => ({
  type: "group",
  id: uid(),
  conjunction: "AND",
  children: [createEmptyStatement()],
});

export const updateNodeById = (
  root: CriteriaGroup,
  targetId: string,
  updater: (node: CriteriaNode) => CriteriaNode,
): CriteriaGroup => {
  if (root.id === targetId) {
    return updater(root) as CriteriaGroup;
  }
  return {
    ...root,
    children: root.children.map((child) => {
      if (child.id === targetId) {
        return updater(child);
      }
      if (child.type === "group") {
        return updateNodeById(child, targetId, updater);
      }
      return child;
    }),
  };
};

export const removeNodeById = (root: CriteriaGroup, targetId: string): CriteriaGroup => {
  return {
    ...root,
    children: root.children
      .filter((child) => child.id !== targetId)
      .map((child) => {
        if (child.type === "group") {
          return removeNodeById(child, targetId);
        }
        return child;
      }),
  };
};

export const addChildToGroup = (
  root: CriteriaGroup,
  groupId: string,
  newChild: CriteriaNode,
): CriteriaGroup => {
  if (root.id === groupId) {
    return { ...root, children: [...root.children, newChild] };
  }
  return {
    ...root,
    children: root.children.map((child) => {
      if (child.type === "group") {
        return addChildToGroup(child, groupId, newChild);
      }
      return child;
    }),
  };
};

/* -------------------------------------------------------------------------- */
/*  Builder value <-> backend `ChecklistItem`-style persistence tree          */
/* -------------------------------------------------------------------------- */

/** The builder's uppercase conjunction to the backend's lowercase operator. */
const conjunctionToOperator = (conjunction: "AND" | "OR"): "and" | "or" =>
  conjunction === "AND" ? "and" : "or";

/** A backend operator back to the builder's uppercase conjunction. */
const operatorToConjunction = (operator: "and" | "or" | "none"): "AND" | "OR" =>
  operator === "or" ? "OR" : "AND";

/**
 * Serialize the builder value to the backend persistence tree (the guideline's
 * `tree`). The single root group is wrapped in a one-element root
 * `ChecklistItem` (empty `statement`, `operator: "none"`) so it becomes the
 * required top-level list, and the root's `conjunction` is distributed to its
 * children (last child = `"none"`).
 *
 * Per level: `conjunction` → `operator` on every child except the last; the last
 * child gets `"none"` (its operator is ignored by the left-associative fold).
 * A group's `label` → the node's `statement`; a statement's `value` → the node's
 * `statement`.
 *
 * `index` is assigned 1-based per level and regenerated by the consumer on save;
 * `id` is preserved from the builder value, so ids survive a save → load
 * round-trip (see the id-stability note on `CriteriaChecklistNode`).
 */
export const criteriaTreeBuilderValueToChecklistTree = (
  root: CriteriaGroup,
): CriteriaChecklistNode[] => {
  const mapGroup = (group: CriteriaGroup): CriteriaChecklistNode => {
    const op = conjunctionToOperator(group.conjunction);
    return {
      index: 0,
      id: group.id,
      statement: group.label ?? "",
      operator: "none",
      children: group.children.map((child, i) => {
        const isLast = i === group.children.length - 1;
        return child.type === "group"
          ? { ...mapGroup(child), operator: isLast ? "none" : op }
          : {
              index: 0,
              id: child.id,
              statement: child.value,
              operator: isLast ? "none" : op,
              children: [],
            };
      }),
    };
  };

  const assignIndex = (nodes: CriteriaChecklistNode[]): CriteriaChecklistNode[] =>
    nodes.map((node, i) => ({
      ...node,
      index: i + 1,
      children: assignIndex(node.children),
    }));

  return assignIndex([mapGroup(root)]);
};

/**
 * Reconstruct the builder value from a backend persistence tree (the guideline's
 * `tree`) so it can be loaded into the builder.
 *
 * - The canonical save shape is a **single root wrapper** (one element, empty
 *   `statement`, `operator: "none"`). It is unwrapped: its `id` becomes the root
 *   group's `id` and its `children` become the root group's children — so a tree
 *   produced by `criteriaTreeBuilderValueToChecklistTree` round-trips exactly,
 *   id for id.
 * - A list with **no** wrapper, or **more than one** top-level item (such as the
 *   PRD's illustrative two-branch `tree`), has no single parent, so the items are
 *   kept as siblings under a synthetic root. No branch is dropped; the synthetic
 *   root's id is only used in this in-memory value and is discarded on the next
 *   save (which re-wraps the root group).
 *
 * A group's `conjunction` is the first child whose `operator` is not `"none"`
 * (siblings share the level's operator; the last is `"none"`). A level with no
 * such operator (empty children, or `"none"` only) defaults to `"AND"` — including
 * the top level, since the evaluator honors root operators too.
 *
 * `id` is read back unchanged, so node ids stay stable across an edit → save →
 * load cycle (see the id-stability note on `CriteriaChecklistNode`).
 */
export const criteriaChecklistTreeToCriteriaTreeBuilderValue = (
  tree: CriteriaChecklistNode[],
): CriteriaGroup => {
  const isWrapper =
    tree.length === 1 && tree[0]!.statement === "" && tree[0]!.operator === "none";

  const levelConjunction = (children: CriteriaChecklistNode[]): "AND" | "OR" => {
    const firstOp = children.find((child) => child.operator !== "none")?.operator;
    return firstOp ? operatorToConjunction(firstOp) : "AND";
  };

  const toNode = (node: CriteriaChecklistNode): CriteriaNode => {
    if (node.children.length > 0) {
      const group: CriteriaGroup = {
        type: "group",
        id: node.id,
        conjunction: levelConjunction(node.children),
        children: node.children.map(toNode),
      };
      if (node.statement) {
        group.label = node.statement;
      }
      return group;
    }
    return { type: "statement", id: node.id, value: node.statement };
  };

  // Canonical single wrapper: unwrap it so its id/children become the root.
  if (isWrapper) {
    const wrapper = tree[0]!;
    return {
      type: "group",
      id: wrapper.id,
      conjunction: levelConjunction(wrapper.children),
      children: wrapper.children.map(toNode),
    };
  }

  // No wrapper (or several top-level items): fold under a synthetic root.
  return {
    type: "group",
    id: uid(),
    conjunction: levelConjunction(tree),
    children: tree.map(toNode),
  };
};